import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ArrowLeft, Loader2, Plus, Power, Save, Trash2 } from "lucide-react";
import { AppShell } from "@/components/app-shell/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel, PanelHeader } from "@/components/common/Card";
import { StatusDot } from "@/components/common/StatusDot";
import { api, type ApiError } from "@/lib/api";
import { fmtTs } from "@/lib/format";

export const Route = createFileRoute("/sites/$siteKey")({
  component: SiteDetailPage,
});

function SiteDetailPage() {
  const { siteKey } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const siteQ = useQuery({
    queryKey: ["site", siteKey],
    queryFn: () => api.getSite(siteKey),
  });
  const formsQ = useQuery({
    queryKey: ["forms", "site", siteKey],
    queryFn: () => api.listFormsForSite(siteKey),
  });
  const statsQ = useQuery({
    queryKey: ["stats", 7, "site", siteKey],
    queryFn: () => api.stats(7),
  });

  const site = siteQ.data?.site;
  const forms = formsQ.data?.forms ?? [];

  const [draftName, setDraftName] = useState<string | null>(null);
  const name = draftName ?? site?.name ?? "";
  const dirty = site && name !== site.name;

  const update = useMutation({
    mutationFn: (body: { name?: string; enabled?: boolean }) =>
      api.updateSite(siteKey, body),
    onSuccess: ({ site: s }) => {
      qc.setQueryData(["site", siteKey], { ok: true, site: s });
      qc.invalidateQueries({ queryKey: ["sites"] });
      setDraftName(null);
    },
  });

  const del = useMutation({
    mutationFn: () => api.deleteSite(siteKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sites"] });
      navigate({ to: "/sites" });
    },
  });

  const sentByForm = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of statsQ.data?.results ?? []) {
      if (r.outcome === "sent") m.set(r.site_key, (m.get(r.site_key) ?? 0) + r.n);
    }
    return m;
  }, [statsQ.data]);

  if (siteQ.isLoading) {
    return (
      <AppShell title="Site">
        <div className="flex items-center gap-2 px-1 py-8 text-xs text-zinc-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> loading site…
        </div>
      </AppShell>
    );
  }
  if (siteQ.isError || !site) {
    return (
      <AppShell title="Site">
        <div className="rounded-md border border-ember/40 bg-ember/10 px-4 py-3 text-sm text-ember">
          {(siteQ.error as ApiError | undefined)?.error ?? "Site not found."}
        </div>
        <Link
          to="/sites"
          className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-zinc-100"
        >
          <ArrowLeft className="h-3 w-3" /> back to sites
        </Link>
      </AppShell>
    );
  }

  const totalSent = forms.reduce((n, f) => n + (sentByForm.get(f.formKey) ?? 0), 0);

  return (
    <AppShell title={site.name}>
      <Link
        to="/sites"
        className="mb-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-zinc-100"
      >
        <ArrowLeft className="h-3 w-3" /> all sites
      </Link>

      <PageHeader
        eyebrow={site.siteKey}
        title={site.name}
        description={
          <span className="inline-flex items-center gap-2 text-xs text-zinc-500">
            <StatusDot status={site.enabled ? "healthy" : "neutral"} />
            {site.enabled ? "enabled" : "disabled"} · {forms.length} forms ·{" "}
            {totalSent} sent in last 7 days
          </span>
        }
        actions={
          <button
            type="button"
            onClick={() => update.mutate({ enabled: !site.enabled })}
            disabled={update.isPending}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-surface/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:bg-surface disabled:opacity-50"
          >
            <Power className="h-3 w-3" />
            {site.enabled ? "Disable site" : "Enable site"}
          </button>
        }
      />

      <div className="grid grid-cols-12 gap-6">
        <Panel className="col-span-12 overflow-hidden lg:col-span-7">
          <PanelHeader title="Site details" subtitle="Identity" />
          <div className="space-y-4 p-6">
            <label className="block">
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                Site name
              </div>
              <div className="flex gap-2">
                <input
                  value={name}
                  onChange={(e) => setDraftName(e.currentTarget.value)}
                  className="h-9 flex-1 rounded-md border border-border bg-surface/40 px-3 text-sm text-zinc-200 placeholder:text-zinc-500 focus:border-ember/60 focus:outline-none focus:ring-2 focus:ring-ember/20"
                />
                <button
                  type="button"
                  onClick={() => update.mutate({ name: name.trim() })}
                  disabled={!dirty || update.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-ember px-3 py-1.5 text-xs font-semibold text-background ring-1 ring-ember/40 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {update.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5" />
                  )}
                  Save
                </button>
              </div>
            </label>
            <Meta label="Site key" value={site.siteKey} mono />
            <Meta label="Created" value={fmtTs(site.createdAt)} mono />
            <Meta label="Updated" value={fmtTs(site.updatedAt)} mono />
          </div>
        </Panel>

        <Panel className="col-span-12 overflow-hidden border border-ember/30 lg:col-span-5">
          <PanelHeader title="Danger zone" subtitle="Permanent" />
          <div className="space-y-3 p-6">
            <p className="text-[11px] text-zinc-400">
              Deleting this site removes it and all of its forms. Audit events
              stay intact but new submissions will 404.
            </p>
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    `Delete site "${site.name}" and ALL its forms? This cannot be undone.`,
                  )
                )
                  del.mutate();
              }}
              disabled={del.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-ember/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-ember hover:bg-ember/10 disabled:opacity-50"
            >
              {del.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3" />
              )}
              Delete site
            </button>
          </div>
        </Panel>

        <Panel className="col-span-12 overflow-hidden">
          <PanelHeader
            title="Forms"
            subtitle={`${forms.length} configured`}
            action={
              <Link
                to="/forms"
                search={{ site: site.siteKey } as never}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-ember hover:brightness-110"
              >
                <Plus className="h-3 w-3" /> new form
              </Link>
            }
          />
          <div className="overflow-x-auto">
            {forms.length === 0 ? (
              <div className="px-6 py-12 text-center text-xs text-zinc-500">
                No forms yet for this site.
              </div>
            ) : (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="px-6 py-3 font-medium">Form</th>
                    <th className="px-6 py-3 font-medium">Recipients</th>
                    <th className="px-6 py-3 font-medium">Sent (7d)</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {forms.map((f) => (
                    <tr
                      key={f.formKey}
                      className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.03]"
                    >
                      <td className="px-6 py-4">
                        <Link
                          to="/forms/$formKey"
                          params={{ formKey: f.formKey }}
                          className="block"
                        >
                          <div className="text-sm font-medium text-zinc-100 hover:text-ember">
                            {f.name}
                          </div>
                          <div className="mt-0.5 font-mono text-[10px] text-zinc-500">
                            {f.formKey.slice(0, 16)}…
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-zinc-300">
                        {f.recipients.length}
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-zinc-300">
                        {sentByForm.get(f.formKey) ?? 0}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-300">
                          <StatusDot status={f.enabled ? "healthy" : "neutral"} />
                          {f.enabled ? "enabled" : "disabled"}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-mono text-[11px] text-zinc-500">
                        {fmtTs(f.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Panel>
      </div>
    </AppShell>
  );
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="mb-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        {label}
      </div>
      <div
        className={`text-sm text-zinc-200 ${mono ? "font-mono text-[11px]" : ""}`}
      >
        {value}
      </div>
    </div>
  );
}
